import { cloneDeep, mapValues, capitalize } from 'lodash';

export const DISTRICT_UUID_STORAGE_KEY = 'sync_setup_district_uuid';

export type RemindSchool = {
  uuid: string;
  name: string;
};

export type RemindSchools = { [remindSchoolName: string]: RemindSchool };

export type ExternalSchool = {
  remindSchoolUuid: string | null;
  name: string;
  id: string;
};

export type ExternalSchools = { [externalSchoolName: string]: ExternalSchool };

export type ApiSyncType = 'Clever' | 'ClassLink' | 'Aeries';
export type SyncTypeMethod = 'SFTP' | 'APIAuth' | 'AuthCredentials';

export function getApiSyncType(syncType?: string): ApiSyncType {
  switch(syncType?.toLowerCase()) {
    case 'classlink':
      return 'ClassLink';
    case 'aeries':
      return 'Aeries';
    case 'clever':
      return 'Clever';
  }

  return 'Clever';
}

export type SyncSetting = {
  remindSchoolName: string | null;
  remindSchoolUuid: string | null;
  externalSchoolName: string | null;
  externalSchoolId: string | null;
  sync: boolean;
  duplicate: boolean;
};

export type SyncSettings = {
  [externalSchoolName: string]: SyncSetting;
};

export const DEFAULT_SCHOOL_SETTING: SyncSetting = {
  remindSchoolName: null,
  remindSchoolUuid: null,
  externalSchoolName: null,
  externalSchoolId: null,
  sync: false,
  duplicate: false,
};

export type SyncConfiguration = {
  key: string;
  title: string;
  description: string;
};

export const PRIMARY_CONFIG_OPTION: SyncConfiguration = {
  key: 'all_roles_and_classes',
  title: 'All roles and classes',
  description:
    'By creating accounts for teachers, students, and parents, your entire school community will be connected through Remind. Additionally, all classes in your SIS will be rostered.',
};

export const SECONDARY_CONFIG_OPTIONS: SyncConfiguration[] = [
  {
    key: 'students_teachers_and_classes',
    title: 'Students, teachers, and classes, but no parents',
    description: 'Create accounts only for students and teachers in their classes.',
  },
  {
    key: 'all_roles_only',
    title: 'Roles only, no classes',
    description: 'Create accounts for teachers, students, and parents.',
  },
  {
    key: 'teachers_only',
    title: 'Teachers only, no classes',
    description: 'Create accounts for only the teachers at each school.',
  },
  {
    key: 'students_only',
    title: 'Students only, no classes',
    description: 'Create accounts for only the students at each school.',
  },
];

export function updateDuplicateFlags(syncSettings: {
  [key: string]: SyncSetting;
}): { [key: string]: SyncSetting } {
  const newSyncSettings = cloneDeep(syncSettings);

  const seen: SyncSettings = {};
  return mapValues(newSyncSettings, (setting: SyncSetting) => {
    setting.duplicate = false;

    if (!setting.remindSchoolName) {
      return setting;
    }

    if (!!seen[setting.remindSchoolName]) {
      setting.duplicate = true;
      seen[setting.remindSchoolName].duplicate = true;
    } else {
      seen[setting.remindSchoolName] = setting;
    }

    return setting;
  });
}

export function deselectSync(syncSettings: SyncSettings, externalSchoolName: string) {
  const newSyncSchools = cloneDeep(syncSettings);
  delete newSyncSchools[externalSchoolName];
  return updateDuplicateFlags(newSyncSchools);
}

export function changeExternalSchool(
  syncSettings: SyncSettings,
  externalSchool: ExternalSchool,
  remindSchool: RemindSchool | undefined
) {
  const newSyncSettings = cloneDeep(syncSettings);
  newSyncSettings[externalSchool.name] = {
    ...DEFAULT_SCHOOL_SETTING,
    externalSchoolName: externalSchool.name,
    externalSchoolId: externalSchool.id,
    remindSchoolName: remindSchool?.name ?? null,
    remindSchoolUuid: remindSchool?.uuid ?? null,
    sync: !!(remindSchool && externalSchool),
  };

  return updateDuplicateFlags(newSyncSettings);
}

export type ApiEventConfigurations = {
  startAPISetup: string,
  matchSchoolsFromSisDashboard: string,
  alreadyInstalled: string,
  clickExternalAuth: string,
  matchSchools: string,
  matchSchoolsConfirmation: string,
  finished: string,
};

export function getApiEventConfigurations(syncType?: string): ApiEventConfigurations {
  const syncTypeLower = syncType?.toLowerCase() || 'classlink'

  return {
    startAPISetup: `rostering.sis_dashboard_api.${syncTypeLower}.start_setup.click`,
    matchSchoolsFromSisDashboard: `rostering.sis_dashboard_api.${syncTypeLower}.match_schools.click`,
    alreadyInstalled: `${syncType}SyncSetup-alreadyInstalled`,
    clickExternalAuth: `rostering.sync_setup.${syncTypeLower}.moved_to_step.external_oauth.click`,
    matchSchools: `rostering.sync_setup.${syncTypeLower}.moved_to_step.match_schools.view`,
    matchSchoolsConfirmation: `rostering.sync_setup.${syncTypeLower}.moved_to_step.match_schools_confirmation.view`,
    finished: `rostering.sync_setup.${syncTypeLower}.moved_to_step.finished.view`
  }
};

export type SyncSetupConfiguration = {
  [key: string]: {
    requiresOauth: boolean,
    setupSteps: string[],
    eventOrder: string[],
  },
};

export const syncSetupConstants: SyncSetupConfiguration = {
  classlink: {
    requiresOauth: true,
    setupSteps: [
      'oauth_match_schools', 'confirm_matched_schools', 'finished'
    ],
    eventOrder: [
      getApiEventConfigurations('classlink').matchSchools,
      getApiEventConfigurations('classlink').matchSchoolsConfirmation,
      getApiEventConfigurations('classlink').finished,
    ],
  },
  clever: {
    requiresOauth: true,
    setupSteps: [
      'oauth_match_schools', 'confirm_matched_schools', 'finished'
    ],
    eventOrder: [
      getApiEventConfigurations('clever').matchSchools,
      getApiEventConfigurations('clever').matchSchoolsConfirmation,
      getApiEventConfigurations('clever').finished,
    ],
  },
  aeries: {
    requiresOauth: false,
    setupSteps: [
      'oauth_match_schools', 'confirm_matched_schools', 'finished'
    ],
    eventOrder: [
      getApiEventConfigurations('aeries').matchSchools,
      getApiEventConfigurations('aeries').matchSchoolsConfirmation,
      getApiEventConfigurations('aeries').finished,
    ],
  },
};
